// Pagos por lote al banco. Control Bancario → Tesorería.
//
// El flujo es el de la vida real, y por eso son cuatro pasos y no uno:
//   armar el lote → exportar el archivo → subirlo al portal del banco →
//   volver aquí y confirmar lo que el banco acreditó de verdad.
// El paso de confirmación existe porque el banco rechaza cuentas: el sistema no
// puede dar por pagada una factura sólo porque se mandó la instrucción.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Check, ChevronRight, Download, Layers, Loader2, Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, cb, cbRpc, fieldCls, money, type CuentaMini } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

// ---------------------------------------------------------------- tipos -----
interface Candidata {
  id: string; serie_comprobante: string | null; fecha_emision: string; fecha_vencimiento: string | null;
  beneficiario: string; identificacion: string | null;
  banco_nombre: string | null; banco_tipo_cuenta: string | null; banco_cuenta: string | null;
  saldo: number; requiere_aprobacion: boolean;
}
interface OrdenCandidata {
  id: string; numero: string; fecha: string; beneficiario: string; monto: number;
  concepto: string | null; documento_ref: string | null; forma_pago: string;
  identificacion: string | null; banco_nombre: string | null;
  banco_tipo_cuenta: string | null; banco_cuenta: string | null;
}
interface LoteFila {
  id: string; numero: string; fecha: string; estado: string; forma_pago: string; concepto: string | null;
  enviado_at: string | null; pagado_at: string | null; motivo: string | null;
  cuenta_alias: string; cuenta_numero: string;
  items: number; total: number; pagados: number; rechazados: number; sin_cuenta: number;
}
interface LoteItem {
  id: string; ticket_buy_id: string | null; orden_id: string | null;
  beneficiario: string; identificacion: string | null;
  banco_nombre: string | null; banco_tipo_cuenta: string | null; banco_cuenta: string | null;
  documento_ref: string | null; monto: number; estado: string; motivo: string | null;
}

const ESTADOS: Record<string, { l: string; c: string }> = {
  borrador: { l: 'Borrador', c: 'bg-muted text-muted-foreground' },
  enviado:  { l: 'Enviado al banco', c: 'bg-blue-500/10 text-blue-600' },
  pagado:   { l: 'Pagado', c: 'bg-green-500/10 text-green-600' },
  anulado:  { l: 'Anulado', c: 'bg-red-500/10 text-red-600' },
};
const Chip = ({ estado }: { estado: string }) => {
  const e = ESTADOS[estado] ?? { l: estado, c: 'bg-muted' };
  return <span className={`px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${e.c}`}>{e.l}</span>;
};

// Anti CSV/formula injection, igual que en las acreditaciones de Nómina: una
// celda que empiece por = + - @ se neutraliza para que Excel no la evalúe.
function descargarCsv(nombre: string, filas: string[][]) {
  const safe = (c: string) => { const v = c ?? ''; return /^[=+\-@\t\r]/.test(v) ? `'${v}` : v; };
  const csv = filas.map((f) => f.map((c) => `"${safe(c).replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ------------------------------------------------------- armar el lote ------
function NuevoLote({ onClose, onCreado }: { onClose: () => void; onCreado: () => void }) {
  const [fecha, setFecha] = useState(fechaLocal());
  const [cuentaId, setCuentaId] = useState('');
  const [concepto, setConcepto] = useState('');
  const [busq, setBusq] = useState('');
  const [venceHasta, setVenceHasta] = useState('');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [selOrd, setSelOrd] = useState<Set<string>>(new Set());

  // Misma consulta que usa Tesorería para su selector de cuentas.
  const { data: cuentas = [] } = useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
  const { data, isFetching } = useQuery<{ facturas: Candidata[]; ordenes: OrdenCandidata[] }>({
    queryKey: ['cb-lote-cand', busq, venceHasta],
    queryFn: () => cbRpc('cb_lote_candidatos', { _busq: busq || null, _vence_hasta: venceHasta || null }),
  });
  const facturas = data?.facturas ?? [];
  const ordenes = data?.ordenes ?? [];

  const crear = useMutation({
    mutationFn: () => cbRpc('cb_lote_crear', {
      _fecha: fecha, _cuenta_id: cuentaId, _tickets: [...sel], _ordenes: [...selOrd],
      _forma_pago: 'transferencia', _concepto: concepto || null,
    }),
    onSuccess: (r: any) => { toast.success(`Lote ${r.numero} creado con ${r.items} pago(s)`); onCreado(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const alternar = (s: Set<string>, set: (v: Set<string>) => void, id: string) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); set(n);
  };
  const total = facturas.filter((f) => sel.has(f.id)).reduce((a, f) => a + Number(f.saldo), 0)
              + ordenes.filter((o) => selOrd.has(o.id)).reduce((a, o) => a + Number(o.monto), 0);
  const n = sel.size + selOrd.size;

  return (
    <Modal title="Nuevo lote de pagos" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Campo label="Fecha de pago"><input type="date" className={fieldCls} value={fecha} onChange={(e) => setFecha(e.target.value)} /></Campo>
          <Campo label="Cuenta de la que sale el dinero">
            <select className={`${fieldCls} w-72`} value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {cuentas.map((c) => <option key={c.id} value={c.id}>{c.alias} · {c.numero}</option>)}
            </select>
          </Campo>
          <Campo label="Concepto (opcional)" className="flex-1 min-w-[16rem]">
            <input className={fieldCls} value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Corrida de pagos quincena…" />
          </Campo>
        </div>

        <div className="flex flex-wrap items-end gap-3 border-t pt-3">
          <Campo label="Buscar proveedor o documento"><input className={`${fieldCls} w-64`} value={busq} onChange={(e) => setBusq(e.target.value)} /></Campo>
          <Campo label="Vence hasta"><input type="date" className={fieldCls} value={venceHasta} onChange={(e) => setVenceHasta(e.target.value)} /></Campo>
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
        </div>

        <div className="max-h-[40vh] overflow-y-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card border-b">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-2 px-2 w-8"></th><th className="py-2 px-2">Proveedor</th>
                <th className="py-2 px-2">Documento</th><th className="py-2 px-2">Vence</th>
                <th className="py-2 px-2">Cuenta bancaria</th><th className="py-2 px-2 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {ordenes.length > 0 && (
                <tr><td colSpan={6} className="bg-muted/50 px-2 py-1 text-xs font-semibold">
                  Órdenes de pago aprobadas ({ordenes.length}) — así entra lo que supera el monto máximo sin aprobación
                </td></tr>
              )}
              {ordenes.map((o) => (
                <tr key={o.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="py-1.5 px-2"><input type="checkbox" checked={selOrd.has(o.id)} onChange={() => alternar(selOrd, setSelOrd, o.id)} /></td>
                  <td className="py-1.5 px-2">{o.beneficiario}</td>
                  <td className="py-1.5 px-2 font-mono text-xs">{o.numero}</td>
                  <td className="py-1.5 px-2">{o.fecha}</td>
                  <td className="py-1.5 px-2 font-mono text-xs">{o.banco_cuenta ?? <span className="text-pendiente">Sin cuenta</span>}</td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums">{money(o.monto)}</td>
                </tr>
              ))}

              {facturas.length > 0 && (
                <tr><td colSpan={6} className="bg-muted/50 px-2 py-1 text-xs font-semibold">Facturas por pagar ({facturas.length})</td></tr>
              )}
              {facturas.map((f) => (
                <tr key={f.id} className={`border-b last:border-0 ${f.requiere_aprobacion ? 'opacity-60' : 'hover:bg-muted/30'}`}>
                  <td className="py-1.5 px-2">
                    <input type="checkbox" disabled={f.requiere_aprobacion} checked={sel.has(f.id)}
                      onChange={() => alternar(sel, setSel, f.id)}
                      title={f.requiere_aprobacion ? 'Supera el monto máximo sin aprobación: genera una orden de pago y hazla aprobar' : undefined} />
                  </td>
                  <td className="py-1.5 px-2">{f.beneficiario}
                    {f.requiere_aprobacion && <div className="text-xs text-pendiente">Requiere orden de pago aprobada</div>}
                  </td>
                  <td className="py-1.5 px-2 font-mono text-xs">{f.serie_comprobante}</td>
                  <td className="py-1.5 px-2">{f.fecha_vencimiento ?? '—'}</td>
                  <td className="py-1.5 px-2 font-mono text-xs">{f.banco_cuenta ?? <span className="text-pendiente">Sin cuenta</span>}</td>
                  <td className="py-1.5 px-2 text-right font-mono tabular-nums">{money(f.saldo)}</td>
                </tr>
              ))}
              {!facturas.length && !ordenes.length && !isFetching && (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No hay documentos pendientes con esos filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-3 border-t pt-3">
          <div className="text-sm">{n} seleccionado(s) — <span className="font-semibold">{money(total)}</span></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button isLoading={crear.isPending} disabled={!n || !cuentaId} onClick={() => crear.mutate()}>
              <Plus className="w-4 h-4 mr-1" /> Crear lote
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------ detalle del lote ----
function DetalleLote({ loteId, onClose, onCambio }: { loteId: string; onClose: () => void; onCambio: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [rechazos, setRechazos] = useState<Record<string, string>>({});
  const { data, isFetching } = useQuery<{ lote: LoteFila & { moneda: string; notas: string | null }; items: LoteItem[] }>({
    queryKey: ['cb-lote', loteId], queryFn: () => cbRpc('cb_lote_obtener', { _id: loteId }),
  });
  const lote = data?.lote; const items = data?.items ?? [];
  const refrescar = () => { void qc.invalidateQueries({ queryKey: ['cb-lote', loteId] }); onCambio(); };

  const fallo = (e: unknown) => toast.error(getErrorMessage(e));

  const quitar = useMutation({
    mutationFn: (itemId: string) => cbRpc('cb_lote_item_quitar', { _item_id: itemId }),
    onSuccess: () => { toast.success('Pago quitado del lote'); refrescar(); }, onError: fallo,
  });
  const enviar = useMutation({
    mutationFn: () => cbRpc('cb_lote_marcar_enviado', { _id: loteId }),
    onSuccess: () => { toast.success('Lote marcado como enviado al banco'); refrescar(); }, onError: fallo,
  });
  const anular = useMutation({
    mutationFn: (motivo: string) => cbRpc('cb_lote_anular', { _id: loteId, _motivo: motivo }),
    onSuccess: () => { toast.success('Lote anulado'); refrescar(); }, onError: fallo,
  });
  const confirmar = useMutation({
    mutationFn: () => cbRpc('cb_lote_confirmar', {
      _id: loteId,
      _rechazos: Object.entries(rechazos).filter(([, m]) => m.trim())
        .map(([item_id, motivo]) => ({ item_id, motivo })),
    }),
    onSuccess: (r: any) => {
      toast.success(`${r.pagados} pago(s) aplicados${r.rechazados ? `, ${r.rechazados} rechazado(s)` : ''} — ${money(r.total_pagado)}`);
      refrescar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const exportar = () => {
    if (!lote) return;
    descargarCsv(`lote-${lote.numero}.csv`, [
      ['Beneficiario', 'Identificación', 'Banco', 'Tipo cuenta', 'N.º cuenta', 'Referencia', 'Monto'],
      ...items.filter((i) => i.estado !== 'rechazado').map((i) => [
        i.beneficiario, i.identificacion ?? '', i.banco_nombre ?? '', i.banco_tipo_cuenta ?? '',
        i.banco_cuenta ?? '', i.documento_ref ?? '', Number(i.monto).toFixed(2),
      ]),
    ]);
  };

  const pendientes = items.filter((i) => i.estado === 'pendiente');
  const totalPend = pendientes.reduce((a, i) => a + Number(i.monto), 0);
  const sinCuenta = pendientes.filter((i) => !i.banco_cuenta).length;
  const editable = lote?.estado === 'borrador';
  const confirmable = lote?.estado === 'borrador' || lote?.estado === 'enviado';

  return (
    <Modal title={lote ? `Lote ${lote.numero}` : 'Lote'} onClose={onClose} wide>
      {dlg.ui}
      {isFetching && !lote && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      {lote && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Chip estado={lote.estado} />
            <span>{lote.fecha}</span>
            <span className="text-muted-foreground">{lote.cuenta_alias} · {lote.cuenta_numero}</span>
            {lote.concepto && <span className="text-muted-foreground">— {lote.concepto}</span>}
          </div>

          {sinCuenta > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-pendiente/40 bg-pendiente/10 p-3 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-pendiente" />
              <span>{sinCuenta} beneficiario(s) sin número de cuenta. Complétalo en su ficha de Partner o quítalos del lote: el banco no puede acreditar sin cuenta.</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportar} disabled={!pendientes.length}>
              <Download className="w-4 h-4 mr-1" /> Exportar archivo para el banco
            </Button>
            {editable && (
              <Button variant="outline" isLoading={enviar.isPending} disabled={!pendientes.length}
                onClick={() => enviar.mutate()}>
                <Send className="w-4 h-4 mr-1" /> Marcar como enviado
              </Button>
            )}
            {confirmable && (
              <Button isLoading={confirmar.isPending} disabled={!pendientes.length}
                onClick={async () => {
                  const nr = Object.values(rechazos).filter((m) => m.trim()).length;
                  const ok = await dlg.confirmar(
                    `¿Aplicar ${pendientes.length - nr} pago(s) por ${money(totalPend)}${nr ? ` y marcar ${nr} como rechazado(s)` : ''}?`,
                    { detalle: 'Esto registra los movimientos bancarios y rebaja la cartera.' });
                  if (ok) confirmar.mutate();
                }}>
                <Check className="w-4 h-4 mr-1" /> Confirmar acreditaciones
              </Button>
            )}
            {confirmable && (
              <Button variant="outline" isLoading={anular.isPending}
                onClick={async () => {
                  const v = await dlg.pedir('Anular lote', [{ key: 'motivo', label: 'Motivo de la anulación', minimo: 1 }]);
                  if (!v) return;
                  anular.mutate(v.motivo);
                }}>
                <Ban className="w-4 h-4 mr-1" /> Anular lote
              </Button>
            )}
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="border-b"><tr className="text-left text-xs text-muted-foreground">
                <th className="py-2 px-2">Beneficiario</th><th className="py-2 px-2">Referencia</th>
                <th className="py-2 px-2">Cuenta</th><th className="py-2 px-2 text-right">Monto</th>
                <th className="py-2 px-2">Estado</th>
                {confirmable && <th className="py-2 px-2">Rechazado por el banco</th>}
                {editable && <th className="py-2 px-2 w-8"></th>}
              </tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} className={`border-b last:border-0 ${i.estado === 'rechazado' ? 'opacity-60' : ''}`}>
                    <td className="py-1.5 px-2">{i.beneficiario}
                      <div className="text-xs text-muted-foreground font-mono">{i.identificacion}</div></td>
                    <td className="py-1.5 px-2 font-mono text-xs">{i.documento_ref}
                      {i.orden_id && <span className="ml-1 text-[10px] px-1 rounded bg-primary/10 text-primary">orden</span>}</td>
                    <td className="py-1.5 px-2 font-mono text-xs">
                      {i.banco_cuenta ?? <span className="text-pendiente">Sin cuenta</span>}
                      {i.banco_nombre && <div className="text-[11px] text-muted-foreground">{i.banco_nombre}</div>}</td>
                    <td className="py-1.5 px-2 text-right font-mono tabular-nums">{money(i.monto)}</td>
                    <td className="py-1.5 px-2">
                      {i.estado === 'pagado' && <span className="text-green-600 text-xs">Pagado</span>}
                      {i.estado === 'rechazado' && <span className="text-red-600 text-xs">Rechazado</span>}
                      {i.estado === 'pendiente' && <span className="text-muted-foreground text-xs">Pendiente</span>}
                      {i.motivo && <div className="text-[11px] text-muted-foreground">{i.motivo}</div>}
                    </td>
                    {confirmable && (
                      <td className="py-1.5 px-2">
                        {i.estado === 'pendiente' && (
                          <input className={`${fieldCls} w-48 h-8 text-xs`} placeholder="Motivo si lo rechazó"
                            value={rechazos[i.id] ?? ''}
                            onChange={(e) => setRechazos((r) => ({ ...r, [i.id]: e.target.value }))} />
                        )}
                      </td>
                    )}
                    {editable && (
                      <td className="py-1.5 px-2">
                        <button title="Quitar del lote" onClick={() => quitar.mutate(i.id)}
                          className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t font-semibold">
                <td className="py-2 px-2" colSpan={3}>Total pendiente de acreditar</td>
                <td className="py-2 px-2 text-right font-mono tabular-nums">{money(totalPend)}</td>
                <td colSpan={3}></td>
              </tr></tfoot>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------ pantalla ------
export function PagosLote() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('');
  const [nuevo, setNuevo] = useState(false);
  const [detalle, setDetalle] = useState('');
  const { data: lotes = [], isFetching } = useQuery<LoteFila[]>({
    queryKey: ['cb-lotes', estado],
    queryFn: () => cbRpc<LoteFila[]>('cb_lotes', { _estado: estado || null }),
  });
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['cb-lotes'] });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-semibold">Pagos por Lote</h1>
        <Button onClick={() => setNuevo(true)}><Plus className="w-4 h-4 mr-1" /> Nuevo lote</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Paga muchas facturas de una vez: arma el lote, exporta el archivo para el portal del banco y vuelve
        aquí a confirmar lo que el banco acreditó. Una factura sólo se da por pagada al confirmar, nunca al
        mandar la instrucción.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <Campo label="Estado">
          <select className={`${fieldCls} w-48`} value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(ESTADOS).map(([v, e]) => <option key={v} value={v}>{e.l}</option>)}
          </select>
        </Campo>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-primary mb-2" />}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <Layers className="w-4 h-4" /> Lotes ({lotes.length})
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">N°</th><th className="py-2 pr-3">Lote</th><th className="py-2 pr-3">Fecha</th>
              <th className="py-2 pr-3">Cuenta</th><th className="py-2 pr-3 text-right">Pagos</th>
              <th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3"></th>
            </tr></thead>
            <tbody>
              {lotes.map((l, i) => (
                <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setDetalle(l.id)}>
                  <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-mono">{l.numero}
                    {l.concepto && <div className="text-xs text-muted-foreground font-sans">{l.concepto}</div>}</td>
                  <td className="py-2 pr-3">{l.fecha}</td>
                  <td className="py-2 pr-3">{l.cuenta_alias}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{l.items}
                    {l.rechazados > 0 && <span className="ml-1 text-xs text-red-600">({l.rechazados} rech.)</span>}
                    {l.sin_cuenta > 0 && l.estado === 'borrador' && <span className="ml-1 text-xs text-pendiente">({l.sin_cuenta} sin cuenta)</span>}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(l.total)}</td>
                  <td className="py-2 pr-3"><Chip estado={l.estado} /></td>
                  <td className="py-2 pr-3"><ChevronRight className="w-4 h-4 text-muted-foreground" aria-hidden /></td>
                </tr>
              ))}
              {!lotes.length && !isFetching && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">
                  Aún no hay lotes. Pulsa «Nuevo lote» para armar la próxima corrida de pagos.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {nuevo && <NuevoLote onClose={() => setNuevo(false)} onCreado={refrescar} />}
      {detalle && <DetalleLote loteId={detalle} onClose={() => setDetalle('')} onCambio={refrescar} />}
    </div>
  );
}
